import {
  CHAT_LOBBY_PRESENCE_WINDOW_MS,
  type ChatPeerParticipant,
  type ChatRole,
  type ChatRoomStatus,
  type ChatSelfParticipant,
} from '@matter_extractor/shared';

/**
 * What the lobby polls at, and what it says (chat spec §4, §6, §7).
 *
 * PURE and tested, the régime `overviewRefetchInterval` and `chatRoomPhase`
 * already hold in this package. It holds two things that are worth a test each
 * and are worthless inside a component nothing can render without a router and
 * a server:
 *
 *   - **the cadence**, including when it STOPS. A lobby left open on a Friday
 *     evening must not spend the weekend asking.
 *   - **the wording decision** - what this side is waiting on, and how the
 *     other side reads. This is where §6 is either honoured or lost, and it is
 *     three lines of arithmetic that a screen would bury.
 *
 * It does NOT duplicate `chat/lobby.ts` on the backend. That module decides
 * TRANSITIONS - when the conversation opens, which gesture is refused - on both
 * participant rows whole. This one decides SENTENCES, from the projection, and
 * the projection has already dropped the peer's `consentedAt` (§6). The split
 * is what makes a refusal invisible by construction rather than by care: the
 * screen cannot show what it was never sent.
 */

/**
 * ~1500 ms (§7). Presence tolerates the second and a half; a conversation does
 * not, which is why the chat is a socket and this is a poll.
 */
export const CHAT_LOBBY_REFRESH_MS = 1500;

/**
 * When the room re-asks for itself, or `false` when there is nothing left to
 * wait for.
 *
 * Two stops, and both are « nothing to wait for » rather than a cadence:
 *
 *   - **the room left the lobby.** Past door 5 the socket carries the
 *     conversation and the REST read is a resynchronisation, not a poll; past
 *     door 6 there is nothing coming at all.
 *   - **the entry window closed.** The next call would answer 410 whatever we
 *     do, and the screen behind it is already S8. Polling a dead link at
 *     1500 ms for as long as a tab stays open is the load generator
 *     `overviewRefetchInterval` exists to refuse - 24 hours of it is 57 600
 *     requests that cannot change a thing.
 *
 * `undefined` on both inputs is the state before the first answer lands, and it
 * also stops: a first fetch that failed is retried by the person, on the button
 * `ChatUnavailable` renders, and a silent loop under her would make that button
 * lie about what it does.
 */
export function chatLobbyRefetchInterval(input: {
  status: ChatRoomStatus | undefined;
  /** Null when the stored creation stamp could not be parsed - see the room schema. */
  expiresAt: string | null | undefined;
  now: number;
}): number | false {
  if (input.status !== 'open') return false;
  if (input.expiresAt !== null && input.expiresAt !== undefined) {
    const expiry = Date.parse(input.expiresAt);
    // An unparseable stamp keeps polling, the soft-fail direction the backend
    // already chose for the same value: at worst a lobby asks a little longer.
    if (!Number.isNaN(expiry) && input.now >= expiry) return false;
  }
  return CHAT_LOBBY_REFRESH_MS;
}

/**
 * How the other side reads in the room panel - and it reads PRESENCE, never a
 * consent.
 *
 * `never-seen` and `away` are not the same thing and §4 needs both: a
 * disconnection is displayed (« la déconnexion s'affiche là »), while somebody
 * who has not opened her link yet is simply awaited. Neither says anything
 * about the box: someone who arrived and did not tick polls exactly like
 * someone who ticked and is not ready, so both read `present`, which is §6.
 */
export type ChatPeerPresence = 'never-seen' | 'present' | 'away';

export function chatPeerPresence(lastSeenAt: string | null, now: number): ChatPeerPresence {
  if (lastSeenAt === null) return 'never-seen';
  const seen = Date.parse(lastSeenAt);
  // Unparseable reads as never-seen rather than present, the direction
  // `chatParticipantPresent` takes on the server for the same reason: claiming
  // somebody is in the room when the stamp is unreadable is the costly error.
  if (Number.isNaN(seen)) return 'never-seen';
  return now - seen < CHAT_LOBBY_PRESENCE_WINDOW_MS ? 'present' : 'away';
}

/**
 * What this side is waiting on, or null when the conversation is opening.
 *
 * `peer` is ONE value for three situations - never arrived, arrived without
 * ticking, ticked without being ready - and the type is where §6 is enforced:
 * « pas coché » and « jamais arrivé » cannot be worded differently by a screen
 * that was never told which of them it is looking at.
 */
export type ChatLobbyWait = 'own-consent' | 'own-ready' | 'peer' | null;

/** Everything the two views of the lobby decide from. */
export type ChatLobbySurface = {
  consented: boolean;
  ready: boolean;
  /**
   * Whether the ready gesture is offered at all (§4.2, §4.4).
   *
   * Own consent AND the peer having shown up ONCE - door 3 sits before door 4,
   * and the maquette's S1 is a ticked box above a dark button beside « En
   * attente de l'interviewer ». « Once » and not « right now », because
   * `lastSeenAt !== null` is monotone: a button that went dark again on every
   * late poll would be the screen receding one gesture at a time.
   */
  readyEnabled: boolean;
  peer: ChatPeerPresence;
  peerReady: boolean;
  waiting: ChatLobbyWait;
};

export function chatLobbySurface(
  room: { self: ChatSelfParticipant; peer: ChatPeerParticipant },
  now: number,
): ChatLobbySurface {
  const consented = room.self.consentedAt !== null;
  const ready = room.self.readyAt !== null;
  const peerReady = room.peer.readyAt !== null;
  const peer = chatPeerPresence(room.peer.lastSeenAt, now);
  const peerHasArrived = room.peer.lastSeenAt !== null;
  const readyEnabled = consented && peerHasArrived;

  return {
    consented,
    ready,
    readyEnabled,
    peer,
    peerReady,
    waiting: !consented
      ? 'own-consent'
      : ready && peerReady
        ? null
        : ready || !peerHasArrived
          ? 'peer'
          : 'own-ready',
  };
}

/**
 * The name of a ROLE, never of a person (§4.3).
 *
 * No identity is collected here, so there is nothing that could leak - on the
 * condition that nothing is invented. These two words are the whole vocabulary
 * the lobby has for the other chair.
 */
export const CHAT_ROLE_LABELS: Readonly<Record<ChatRole, string>> = {
  interviewer: 'Interviewer',
  guest: 'Invité',
};

/**
 * The waiting line, per role - and it says the same thing for all three reasons
 * the other side might not be ready (§6).
 */
export const CHAT_PEER_WAITING: Readonly<Record<ChatRole, string>> = {
  interviewer: 'En attente de l’interviewer',
  guest: 'En attente de l’invité',
};

/**
 * The one thing the lobby says about the other side beyond « en attente » (§4).
 *
 * It reads a lapsed POLL and never a box: somebody who arrived and did not tick
 * is still polling, so she reads « connecté » like anybody else, and only
 * leaving the room is visible. That is the line §6 draws - presence is a fact
 * about the connection, a consent is not.
 */
export const CHAT_PEER_DISCONNECTED: Readonly<Record<ChatRole, string>> = {
  interviewer: 'L’interviewer s’est déconnecté',
  guest: 'L’invité s’est déconnecté',
};

/**
 * The room's query key, spelled ONCE.
 *
 * The shell polls it and the lobby writes each gesture's answer straight into
 * it, so two spellings would give the lobby a cache the shell never reads - and
 * the screen would only catch up on the next poll, which is exactly the 1500 ms
 * of lag a gesture must not have.
 */
export const chatRoomQueryKey = (token: string): readonly string[] => ['chat', token];

/** The consent block's key. Fetched once beside the room, never polled. */
export const chatConsentQueryKey = (token: string): readonly string[] => ['chat', token, 'consent'];
